import { World, Bodies } from "fast-2d";
import type { BenchScene } from "./scenes";
import type { EngineRunner } from "./runner";

/** Build a Fast2D runner for a benchmark scene (sleeping off for fair throughput). */
export function createFast2DRunner(scene: BenchScene): EngineRunner {
	// Fast2D runs its tuned default solver (TGS soft substeps) — the same config a
	// real user gets out of the box. We don't hand-match iteration counts to other
	// engines: under substepping, velocity iterations run per substep, so copying
	// Matter's 4/6 would do 3x the work. Each engine here runs at its own default.
	const world = new World({
		gravity: [0, scene.gravityY],
		sleep: false,
	});
	let count = 0;

	scene.build((s) => {
		if (s.kind === "circle") {
			world.add(
				Bodies.circle({
					position: [s.x, s.y],
					radius: s.r!,
					isStatic: s.isStatic,
					friction: s.friction ?? 0.4,
					restitution: s.restitution ?? 0,
				}),
			);
		} else if (s.kind === "triangle") {
			world.add(
				Bodies.polygon({
					position: [s.x, s.y],
					vertices: s.verts!,
					isStatic: s.isStatic,
					friction: s.friction ?? 0.4,
					restitution: s.restitution ?? 0,
				}),
			);
		} else {
			world.add(
				Bodies.rectangle({
					position: [s.x, s.y],
					width: s.w!,
					height: s.h!,
					isStatic: s.isStatic,
					friction: s.friction ?? 0.4,
					restitution: s.restitution ?? 0,
				}),
			);
		}
		count++;
	});

	return {
		name: "Fast2D",
		get bodyCount() {
			return count;
		},
		step(dt: number): void {
			world.step(dt);
		},
		forEachBody(cb): void {
			const bodies = world.bodies;
			for (let i = 0; i < bodies.length; i++) {
				const b = bodies[i]!;
				b.synchronize();
				if (b.isCircle) {
					cb({
						kind: "circle",
						x: b.position.x,
						y: b.position.y,
						r: b.radius,
						angle: b.angle,
						isStatic: b.isStatic,
					});
				} else {
					const v = b.worldVerts;
					const verts: number[] = new Array(v.length);
					for (let j = 0; j < v.length; j++) verts[j] = v[j]!;
					cb({ kind: "poly", verts, isStatic: b.isStatic });
				}
			}
		},
		dispose(): void {
			while (world.bodies.length > 0) world.remove(world.bodies[world.bodies.length - 1]!);
		},
	};
}
